import React from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Linkedin, Github, ExternalLink } from 'lucide-react';

const AboutSection = () => {
  const focusAreas = [
    {
      title: 'Generative AI Integration',
      description: 'Building intelligent systems with LLMs and ML models',
      color: 'from-purple-500 to-pink-500',
      icon: '🤖'
    },
    {
      title: 'Cloud-Native Architecture',
      description: 'Scalable microservices on AWS, Docker, and Kubernetes',
      color: 'from-blue-500 to-cyan-500',
      icon: '☁️'
    },
    {
      title: 'Healthcare Technology',
      description: 'Analytics platforms for medical and healthcare domains',
      color: 'from-green-500 to-emerald-500',
      icon: '🏥'
    },
    {
      title: 'Team Leadership & Mentoring',
      description: 'Developing talent and driving engineering excellence',
      color: 'from-orange-500 to-red-500',
      icon: '👥'
    }
  ];

  const socialLinks = [
    {
      name: 'LinkedIn',
      url: 'https://www.linkedin.com/in/sonyjoyalphonsa/',
      icon: Linkedin,
      color: 'hover:text-blue-400'
    },
    {
      name: 'GitHub',
      url: 'https://github.com/sonyalphy',
      icon: Github,
      color: 'hover:text-gray-300'
    },
    {
      name: 'Medium',
      url: 'https://medium.com/@scholarsonyalphy2022',
      icon: ExternalLink,
      color: 'hover:text-green-400'
    }
  ];

  return (
    <section id="about" className="py-20 relative">
      <div className="container mx-auto px-6 z-10 relative">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            About <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Me</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left Column - About Text */}
          <div className="space-y-6">
            <p className="text-lg text-gray-300 leading-relaxed text-justify">
              I'm a passionate <span className="text-white font-semibold">Full Stack Software Engineer</span> with over{' '}
              <span className="text-blue-400 font-semibold">7 years of experience</span> building scalable, customer-centric applications. 
              My expertise spans the entire technology stack, from{' '}
              <span className="text-blue-400">ReactJS frontends</span> to{' '}
              <span className="text-blue-400">Java Spring Boot backends</span>, all orchestrated on{' '}
              <span className="text-blue-400">AWS cloud infrastructure</span>.
            </p>

            <p className="text-lg text-gray-300 leading-relaxed text-justify">
              Currently leading development at <span className="text-white font-semibold">UST (Philips)</span>, I specialize 
              in creating intelligent healthcare analytics platforms that leverage{' '}
              <span className="text-green-400">generative AI</span> and{' '}
              <span className="text-green-400">real-time data processing</span>. My 
              recent work includes architecting systems that improved throughput by 40% and reduced deployment cycles by 30%.
            </p>

            <p className="text-lg text-gray-300 leading-relaxed text-justify">
              Beyond coding, I'm passionate about <span className="text-orange-400">mentoring emerging developers</span> and 
              staying at the forefront of technology trends, particularly in{' '}
              <span className="text-cyan-400">AI/ML integration</span>,{' '}
              <span className="text-cyan-400">cloud-native architectures</span>, and{' '}
              <span className="text-cyan-400">DevOps practices</span>.
            </p>

            {/* Social Links */}
            <div className="flex items-center space-x-6 pt-6">
              <span className="text-gray-400 font-medium">Connect with me:</span>
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-10 h-10 rounded-full bg-gray-800/50 flex items-center justify-center text-gray-400 ${social.color} transition-all duration-200 hover:scale-110 hover:bg-gray-700/50 border border-gray-700/50`}
                  title={social.name}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>

            {/* Key Achievements */}
            <div className="grid grid-cols-2 gap-4 mt-8">
              <div className="bg-gray-800/40 rounded-xl p-4 border border-gray-700/50 backdrop-blur-sm">
                <div className="text-2xl font-bold text-yellow-400">40%</div>
                <div className="text-sm text-gray-400">Performance Improvement</div>
                <div className="text-xs text-gray-500 mt-1">Average across projects</div>
              </div>
              <div className="bg-gray-800/40 rounded-xl p-4 border border-gray-700/50 backdrop-blur-sm">
                <div className="text-2xl font-bold text-green-400">99.9%</div>
                <div className="text-sm text-gray-400">Project Success Rate</div>
                <div className="text-xs text-gray-500 mt-1">Uptime in production systems</div>
              </div>
            </div>
          </div>

          {/* Right Column - Focus Areas */}
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-white mb-6">Current Focus Areas</h3>
            
            {focusAreas.map((area, index) => (
              <Card key={index} className="bg-gray-800/40 border-gray-700/50 p-6 hover:bg-gray-800/60 transition-all duration-300 hover:scale-105 backdrop-blur-sm">
                <div className="flex items-start space-x-4">
                  <div className="text-2xl">{area.icon}</div>
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-white mb-2">{area.title}</h4>
                    <p className="text-gray-400 text-sm text-justify">{area.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;